import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api, AED_short } from '../../lib/api';
import MobileShell from '../../components/MobileShell';
import { Heart, MapPin, Clock, Gavel, Search, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

function timeLeft(iso) {
  if (!iso) return { text: '—', urgent: false, ended: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { text: 'Ended', urgent: false, ended: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return { text: `${d}d ${h}h`, urgent: d < 2, ended: false };
  if (h >= 1) return { text: `${h}h ${m}m`, urgent: true, ended: false };
  return { text: `${m}m`, urgent: true, ended: false };
}

const FILTERS = [
  { key: 'all',     label: 'Live' },
  { key: 'watched', label: 'Watchlist' },
  { key: 'ending',  label: 'Ending' },
  { key: 'new',     label: 'New' },
  { key: 'past',    label: 'Past' },
];

/**
 * MobileAuctions — list only. Detail opens on a dedicated page (/m/auctions/:id)
 * instead of an in-page overlay modal (per user's mobile philosophy).
 */
export default function MobileAuctions() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [, tick] = useState(0);
  const nav = useNavigate();
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/auctions'); setList(r.data || []); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => tick(x => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Deep-link support: /m/auctions?open=<external_id> → go to detail page.
  useEffect(() => {
    const id = params.get('open');
    if (id) nav(`/m/auctions/${id}`, { replace: true });
  }, [params, nav]);

  const toggleWatch = async (a, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    try {
      const r = await api.post(`/auctions/${a.external_id}/watch`);
      setList(xs => xs.map(x => x.external_id === a.external_id ? { ...x, is_watched: r.data.watched } : x));
      toast.success(r.data.watched ? 'Watching' : 'Removed');
    } catch { toast.error('Failed'); }
  };

  const filtered = useMemo(() => {
    let xs = list;
    const now = Date.now();
    // By default HIDE ended auctions (user complaint — clutter). Only show them
    // when the user explicitly picks the 'past' filter.
    if (filter === 'past') {
      xs = xs.filter(x => new Date(x.auction_end_at).getTime() <= now);
      // Newest ended first
      xs = [...xs].sort((a, b) =>
        new Date(b.auction_end_at).getTime() - new Date(a.auction_end_at).getTime()
      );
    } else {
      xs = xs.filter(x => new Date(x.auction_end_at).getTime() > now);
      if (filter === 'watched') xs = xs.filter(x => x.is_watched);
      if (filter === 'ending') xs = xs.filter(x => {
        const t = new Date(x.auction_end_at).getTime() - now;
        return t > 0 && t < 3 * 86400000;
      });
      if (filter === 'new') {
        const cutoff = now - 24 * 3600 * 1000;
        xs = xs.filter(x => new Date(x.fetched_at).getTime() >= cutoff);
      }
    }
    const s = q.trim().toLowerCase();
    if (s) xs = xs.filter(x => (x.title + ' ' + (x.address || '') + ' ' + (x.area || '')).toLowerCase().includes(s));
    return xs;
  }, [list, filter, q]);

  return (
    <MobileShell title="Auctions" noHeader>
      <div className="pt-3 pb-2 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center shadow-md">
          <Gavel className="text-white" size={18}/>
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-3xl leading-none">Auctions</h1>
          <div className="text-[11px] text-slate-500 mt-1">{filtered.length} of {list.length} · Emirates Auction live</div>
        </div>
      </div>

      <div className="relative mb-3 mt-2">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search…"
          data-testid="m-auctions-search"
          className="w-full pl-10 pr-3 py-3 rounded-2xl bg-white/85 border border-white/70 text-sm outline-none focus:ring-2 focus:ring-[#679286]/30"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-3 no-scrollbar" data-testid="m-auctions-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`m-auction-filter-${f.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              filter === f.key ? 'bg-[#1A2530] text-white shadow-md' : 'bg-white/70 text-slate-700 border border-white/60'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="rounded-2xl bg-white/60 h-56 animate-pulse"/>)}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 text-sm text-slate-400">
          <Gavel size={28} className="mx-auto mb-2 opacity-40"/>
          No auctions match.
        </div>
      )}

      {!loading && (
        <div className="space-y-3" data-testid="m-auctions-list">
          {filtered.map(a => <MCard key={a.external_id} a={a} onWatch={toggleWatch}/>)}
        </div>
      )}
    </MobileShell>
  );
}

function MCard({ a, onWatch }) {
  const tl = timeLeft(a.auction_end_at);
  const img = (a.images || [])[0];
  const score = a.iris_signals?.hotness_score;
  return (
    <Link
      to={`/m/auctions/${a.external_id}`}
      data-testid={`m-auction-${a.external_id}`}
      className="block rounded-2xl overflow-hidden bg-white/85 border border-white/70 shadow-sm active:scale-[0.98] transition-transform"
    >
      <div className="h-40 bg-gradient-to-br from-slate-200 to-slate-300 relative">
        {img && <img src={img} alt="" className="w-full h-full object-cover" loading="lazy"/>}
        {score != null && score > 0 && (
          <div
            data-testid={`m-auction-score-${a.external_id}`}
            className={`absolute bottom-3 left-3 w-11 h-11 rounded-full grid place-items-center text-white shadow-lg text-center leading-none bg-gradient-to-br ${
              score >= 80 ? 'from-emerald-500 to-teal-600'
              : score >= 65 ? 'from-amber-500 to-orange-500'
              : 'from-slate-400 to-slate-600'
            }`}
          >
            <div>
              <div className="font-serif text-base">{Math.round(score)}</div>
              <div className="text-[6px] uppercase tracking-widest opacity-90">iris</div>
            </div>
          </div>
        )}
        <button
          onClick={(e) => onWatch(a, e)}
          data-testid={`m-watch-${a.external_id}`}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 grid place-items-center shadow-md"
        >
          <Heart size={16} className={a.is_watched ? 'fill-red-500 text-red-500' : 'text-slate-600'}/>
        </button>
        <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest ${
          tl.ended ? 'bg-slate-200 text-slate-500' : tl.urgent ? 'bg-red-500 text-white' : 'bg-slate-900/85 text-white'
        }`}>
          <Clock size={10} className="inline mr-1"/> {tl.text}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-1">
          <MapPin size={11}/> {a.address || a.area || a.emirate || '—'}
        </div>
        <div className="text-sm font-medium text-slate-900 line-clamp-2 min-h-[2.5rem]">{a.title || 'Auction listing'}</div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-slate-500">Current bid</div>
            <div className="font-semibold text-[#679286]">{AED_short(a.current_bid_aed)}</div>
          </div>
          <div>
            <div className="text-slate-500">Bids</div>
            <div className="font-medium">{a.bid_count || 0}</div>
          </div>
        </div>
      </div>
      <div className="px-4 py-2 border-t border-slate-100/70 flex items-center justify-between text-xs text-[#679286] font-medium">
        <span>Open details</span>
        <ChevronRight size={14}/>
      </div>
    </Link>
  );
}
